import { apiClient } from './client';
import type { ChannelType } from '@ixam/shared';

export interface ChannelTestStatus {
  lastTestAt: string | null;
  lastTestSuccess: boolean | null;
  lastTestError: string | null;
}

export interface ChannelDto {
  _id: string;
  name: string;
  type: ChannelType;
  enabled: boolean;
  config: Record<string, unknown>;
  testStatus: ChannelTestStatus;
  createdAt: string;
  updatedAt: string;
}

export interface TelegramConfig {
  botToken: string;
  chatId: string;
  parseMode?: 'HTML' | 'MarkdownV2';
}

export interface SlackConfig {
  webhookUrl: string;
  channel?: string;
}

export type CreateTelegramChannel = {
  name: string;
  type: 'telegram';
  enabled?: boolean;
  config: TelegramConfig;
};

export type CreateSlackChannel = {
  name: string;
  type: 'slack';
  enabled?: boolean;
  config: SlackConfig;
};

export type CreateChannelInput = CreateTelegramChannel | CreateSlackChannel;

export interface UpdateChannelInput {
  name?: string;
  enabled?: boolean;
  config?: Record<string, unknown>;
}

export async function listChannels(): Promise<ChannelDto[]> {
  const res = await apiClient.get<{ items: ChannelDto[] }>('/channels');
  return res.data.items;
}

export async function createChannel(input: CreateChannelInput): Promise<ChannelDto> {
  const res = await apiClient.post<{ channel: ChannelDto }>('/channels', input);
  return res.data.channel;
}

export async function updateChannel(
  id: string,
  input: UpdateChannelInput,
): Promise<ChannelDto> {
  const res = await apiClient.put<{ channel: ChannelDto }>(`/channels/${id}`, input);
  return res.data.channel;
}

export async function deleteChannel(id: string): Promise<void> {
  await apiClient.delete(`/channels/${id}`);
}

export interface TestChannelResult {
  result: { success: boolean; error?: string | null };
  channel: ChannelDto;
}

export async function testChannel(id: string): Promise<TestChannelResult> {
  const res = await apiClient.post<TestChannelResult>(`/channels/${id}/test`);
  return res.data;
}
